/**
 * 더딥스 수중 스포츠 커리큘럼 프로모션 콘텐츠.
 *
 * 이 파일은 화면에 노출되는 문구·링크·이미지만 담는다. 마크업/스타일은 컴포넌트가 담당한다.
 * 문구 안에서 쓸 수 있는 표기는 두 가지뿐이다.
 *   - `\n`     : 줄바꿈(<br />)
 *   - `**...**`: 강조(<b>)
 * 새 프로모션 페이지를 만들 때의 규칙은 ../PROMOTION_PAGE_GUIDE.md 참고.
 */

import logoNavy from "assets/images/promotion/curriculum/logo_deeps_navy.png";
import logoWhite from "assets/images/promotion/curriculum/logo_deeps_white.png";
import freedivingPhoto from "assets/images/promotion/curriculum/freediving.jpg";
import scubaPhoto from "assets/images/promotion/curriculum/scuba.jpg";
import mermaidPhoto from "assets/images/promotion/curriculum/mermaid.jpg";
import oneElevenLogo from "assets/images/promotion/curriculum/one_eleven.png";

export const CURRICULUM_LOGO = {
    navy: logoNavy,
    white: logoWhite,
    alt: "THE DEEPS",
};

/** 종목 공통 하단 CTA. 카카오 상담은 외부 링크라 새 탭으로 연다. */
export const CURRICULUM_ACTIONS = {
    schedule: { label: "클래스 일정 확인하기", to: "/reservation?type=class" },
    kakaoSport: {
        label: "어떤 종목이 맞을지 모르겠다면? 카톡 상담",
        href: "http://pf.kakao.com/_hVnQs/chat",
    },
    kakaoCourse: {
        label: "어떤 과정이 맞을지 모르겠다면? 카톡 상담",
        href: "http://pf.kakao.com/_hVnQs/chat",
    },
};

/** /promotion/class 진입 화면(종목 선택). */
export const CURRICULUM_HUB = {
    seoTitle: "더딥스 수중 스포츠 가이드",
    seoDescription:
        "프리다이빙 · 스쿠버다이빙 · 머메이드. 더딥스의 세 가지 수중 스포츠 커리큘럼을 한눈에 비교하고 나에게 맞는 과정을 찾아보세요.",
    eyebrow: "UNDERWATER SPORTS GUIDE",
    headline: { lead: "어떤 물속을", rest: "만나고 ", accent: "싶으세요?" },
    lede: ["세 가지 길이 있습니다.", "마음이 가는 쪽을 눌러보세요."],
    note: [
        "더딥스의 교육은 PADI · AIDA · SSI 등 다양한 협회 과정으로 진행됩니다.",
        "단계 배정과 진급은 담당 강사의 평가에 따라 안내됩니다.",
    ],
};

export const CURRICULUM_SPORTS = [
    /* ===================== 프리다이빙 ===================== */
    {
        slug: "freediving",
        theme: "freediving",
        darkCard: false,
        name: { ko: "프리다이빙", en: "FREEDIVING" },
        photo: freedivingPhoto,
        hubCard: {
            slogan: "숨 하나로, 가장 자유롭게",
            who: "최소의 장비로 자유롭게 바다를 즐기고 싶은 분",
            desc: "장비에 기대지 않고, 내 몸과 호흡만으로 물속에 머무릅니다.",
            cta: "프리다이빙 커리큘럼 보기",
        },
        page: {
            seoTitle: "더딥스 프리다이빙 커리큘럼",
            seoDescription:
                "원데이 체험부터 입문 · 초급 · 중급 · 고급 · 강사 과정까지. 더딥스 프리다이빙 단계별 커리큘럼과 진급 기준을 안내합니다.",
            eyebrow: "FREEDIVING CURRICULUM GUIDE",
            headline: {
                lead: "프리다이빙, 어떤 과정을",
                rest: "신청해야 할지 ",
                accent: "모르겠다면?",
            },
            lede: [
                "괜찮아요, 이 가이드 하나면 충분해요.",
                "체험부터 강사까지, 순서대로 따라오시면 됩니다.",
            ],
            quickNav: [
                { id: "s-oneday", em: "체험", label: "원데이" },
                { id: "s-basic", em: "LV.1", label: "입문" },
                { id: "s-open", em: "LV.2", label: "입문+초급", hot: true },
                { id: "s-inter", em: "LV.3", label: "중급" },
                { id: "s-adv", em: "LV.4", label: "고급" },
                { id: "s-pro", em: "PRO", label: "강사" },
                { id: "s-special", em: "별도", label: "특강" },
            ],
            surface: "0m · 수면",
            blocks: [
                {
                    kind: "step",
                    id: "s-oneday",
                    zone: "light",
                    node: "·",
                    tag: "EXPERIENCE",
                    title: "원데이 클래스",
                    sub: "체험",
                    rows: [
                        { k: "구성", v: "풀장 **1회 (2시간)** · 하루로 끝나는 체험 과정" },
                        {
                            k: "내용",
                            v: "호흡과 이완의 첫 경험, 물속에서 숨을 참아보는 프리다이빙 맛보기",
                        },
                    ],
                    rec: "프리다이빙, 일단 한 번 해보고 싶은 분 추천!",
                    button: { label: "원데이 클래스 보기", to: "/class/detail/14", tone: "light" },
                },
                {
                    kind: "step",
                    id: "s-basic",
                    zone: "light",
                    node: "1",
                    tag: "STEP 1",
                    title: "입문",
                    rows: [
                        { k: "핵심 학습", v: "호흡과 이완의 기초 · 물 적응 · 기본 핀킥과 잠영" },
                        { k: "구성", v: "기본 이론 교육 + 풀장 교육 **2회** (회당 3시간)" },
                        { k: "수료 기준", v: "기록 기준 없음 — 기초 기술 습득 중심" },
                    ],
                    rec: "물이 처음이어도 괜찮아요.\n프리다이빙의 감각을 만드는 단계.",
                    button: { label: "입문 과정 보기", to: "/class/detail/47", tone: "light" },
                },
                {
                    kind: "step",
                    id: "s-open",
                    zone: "light",
                    variant: "flagship",
                    node: "2",
                    depth: { value: "-10m", label: "DEPTH", tone: "light" },
                    badge: "✦ 가장 많이 선택하는 레벨",
                    tag: "STEP 2",
                    title: "입문+초급",
                    rows: [
                        {
                            k: "핵심 학습",
                            v: "이퀄라이징(압력 평형) · 덕다이빙과 수직 하강 · 버디 시스템과 안전 수칙",
                        },
                        { k: "구성", v: "이론 1회 + 풀장 교육 **3회** (회당 3시간)" },
                        {
                            k: "진급 기준",
                            v: "스태틱 **1분 30초** · 다이나믹 **25m** · 수심 **10m**",
                        },
                    ],
                    benefit: { text: "이 레벨부터 바다 펀다이빙 · 국내외 투어 참여 가능" },
                    rec: "바다에 들어가기 위한 가장 최소한의 레벨.\n뭐부터 시작할지 모르겠다면, 여기서 시작하세요!",
                    button: { label: "입문+초급 과정 보기", to: "/class/detail/10", tone: "gold" },
                },
                {
                    kind: "step",
                    id: "s-inter",
                    zone: "dark",
                    node: "3",
                    depth: { value: "-20m", label: "DEPTH", tone: "white" },
                    tag: "STEP 3",
                    title: "중급",
                    rows: [
                        {
                            k: "핵심 학습",
                            v: "프리폴 진입 · 프렌젤 이퀄라이징 심화\n훈련 테이블을 활용한 셀프 트레이닝",
                        },
                        { k: "구성", v: "이론 1회 + 풀장 교육 **3회** (회당 3시간)" },
                        {
                            k: "진급 기준",
                            v: "스태틱 **2분 30초** · 다이나믹 **50m** · 수심 **20m**",
                        },
                    ],
                    rec: "수심이 목표가 되는 단계.\n취미를 넘어, 진짜 프리다이버로.",
                    button: { label: "중급 과정 보기", to: "/class/detail/11", tone: "deep" },
                },
                {
                    kind: "special",
                    tone: "onemore",
                    side: true,
                    bypass: "바로 진급도 가능",
                    chip: "SPECIALTY · 별도 과정",
                    title: "ONE MORE",
                    titleEn: "인도어 트레이닝",
                    image: { src: oneElevenLogo, alt: "One Eleven" },
                    copy: "기준은 자격증이 아닌, 실력입니다.",
                    desc: "정규 진급 과정이 아닌, 실내(인도어) 풀에서 진행하는 스페셜티 클래스.\n자격증 커리큘럼 없이, 훈련과 기록에만 집중해 실력을 다집니다.",
                    point: "프리다이빙 선수가 직접 알려주는 원포인트 레슨",
                    items: [
                        {
                            num: "Ⅰ",
                            t: "ONE",
                            d: "기본 완성 · 스타트 · 중성부력 → **DYNB 50m**",
                        },
                        {
                            num: "Ⅱ",
                            t: "ELEVEN",
                            d: "피닝 효율 · CO2 테이블 → **노컨트랙션 50m**",
                        },
                        {
                            num: "Ⅲ",
                            t: "ONE ELEVEN",
                            d: "맥스 다이빙 훈련 → **DYNB 100m+**",
                        },
                    ],
                    button: { label: "원모어 트레이닝 보기", to: "/class/detail/5", tone: "violet" },
                },
                {
                    kind: "step",
                    id: "s-adv",
                    zone: "dark",
                    violet: true,
                    node: "4",
                    depth: { value: "-32m", label: "DEPTH", tone: "cyan" },
                    tag: "STEP 4",
                    title: "고급",
                    rows: [
                        {
                            k: "핵심 학습",
                            v: "마우스필 테크닉 · 고수심 트레이닝 이론 · 블랙아웃 대응과 레스큐 심화",
                        },
                        { k: "구성", v: "이론 1회 + 풀장 교육 **4회** (회당 3시간)" },
                        {
                            k: "진급 기준",
                            v: "스태틱 **3분 30초** · 다이나믹 **70m** · 수심 **32m**",
                        },
                        { k: "필수 요건", v: "**EFR(응급처치)** 과정 수료" },
                    ],
                    benefit: { text: "보조 강사 활동이 가능한 특별한 레벨", violet: true },
                    rec: "깊은 수심에 도전하고 버디를 리드하는,\n프리다이버의 완성 단계.",
                    button: { label: "고급 과정 보기", to: "/class/detail/9", tone: "violet" },
                },
                {
                    kind: "step",
                    id: "s-pro",
                    zone: "dark",
                    variant: "final",
                    node: "5",
                    violetNode: true,
                    tag: "PROFESSIONAL",
                    title: "강사 과정",
                    paragraphs: [
                        "배우는 사람에서, 가르치는 사람으로.\n고급 수료 후 도전할 수 있는 프로페셔널 트랙입니다.",
                    ],
                    button: { label: "강사 과정 보기", to: "/class/detail/56", tone: "violet" },
                },
            ],
            apartSections: [
                {
                    id: "s-special",
                    head: "정규 과정은 아니지만, **이런 수업도 있어요**",
                    special: {
                        tone: "teukgang",
                        chip: "SPECIALTY · 정규 커리큘럼과 별개",
                        title: "더딥스 특강",
                        copy: "지금 막힌 부분만, 집중해서 잡습니다.",
                        desc: "진급을 위한 과정이 아니라, 필요한 순간에 골라 듣는 원포인트 수업.\n어느 단계에 계시든, 정규 과정 수강 중에도 들으실 수 있어요.",
                        point: "16m 이상 딥탱크 훈련은 특강에서만 만나실 수 있습니다",
                        items: [
                            { t: "이퀄 특강", d: "5m만 가면 귀가 아파서 멈추시나요?" },
                            { t: "수심 특강", d: "딥탱크에서 더 깊이 내려가 보고 싶으신가요?" },
                            {
                                t: "잠영(다이나믹) 특강",
                                d: "자격증 테스트, 다이나믹 기록이 부족하신가요?",
                            },
                            { t: "사진 특강", d: "남의 사진은 많은데, 내 사진은 없으신가요?" },
                        ],
                        info: "수업 시간 **3시간** · 정원 **최대 4명** · 수강료 **60,000원**\n영상 촬영 및 세션 완료 후 개인 피드백 제공",
                        button: { label: "특강 살펴보기", to: "/class/detail/43", tone: "light" },
                    },
                },
            ],
            faqSections: [
                {
                    title: "자주 묻는 질문",
                    items: [
                        {
                            q: "수영을 못해도 배울 수 있나요?",
                            a: "네, 가능합니다. 프리다이빙은 빠르게 헤엄치는 운동이 아니라 힘을 빼고 물에 뜨는 법을 배우는 운동이에요. 물에 대한 두려움이 있으시다면 원데이 클래스나 입문 과정부터 시작하시길 권해드립니다.",
                        },
                        {
                            q: "자격증이 꼭 있어야 바다에 들어갈 수 있나요?",
                            a: "안전을 위해 입문+초급 이상 자격을 권장하고 있습니다. 이 레벨부터 바다 펀다이빙과 국내외 투어 참여가 가능해요.",
                        },
                        {
                            q: "장비를 미리 사야 하나요?",
                            a: "아니요. 수업에 필요한 장비는 대여해 드리니 수영복만 준비하시면 됩니다. 어떤 장비가 나에게 맞는지는 수업을 들어보신 뒤 결정하시는 편이 좋아요.",
                        },
                        {
                            q: "한 단계를 마치는 데 얼마나 걸리나요?",
                            a: "사람마다 다릅니다. 수업 회차는 정해져 있지만 진급 기준 도달 속도는 개인차가 크기 때문에, 기준에 도달할 때까지 담당 강사와 함께 조율하며 진행합니다.",
                        },
                        {
                            q: "어떤 협회 자격증을 받게 되나요?",
                            a: "더딥스는 PADI · AIDA · SSI 등 여러 협회 과정을 운영하고 있습니다. 회차마다 진행 협회가 다를 수 있으니 신청 전 확인해 주세요.",
                        },
                        {
                            q: "제 레벨에 뭘 들어야 할지 모르겠어요.",
                            a: "카카오톡으로 문의 주시면 지금 상황에 맞는 수업을 안내해 드립니다. 아래 상담 버튼을 눌러주세요.",
                        },
                    ],
                },
            ],
            note: [
                "더딥스의 교육은 PADI · AIDA · SSI 등 다양한 협회 과정으로 진행됩니다.",
                "※ 기록 기준은 협회별로 상이할 수 있으며, 단계 배정과 진급은 담당 강사의 평가에 따라 안내됩니다.",
            ],
        },
    },

    /* ===================== 스쿠버다이빙 ===================== */
    {
        slug: "scuba",
        theme: "scuba",
        darkCard: false,
        name: { ko: "스쿠버다이빙", en: "SCUBA DIVING" },
        photo: scubaPhoto,
        hubCard: {
            slogan: "바다를 구경하는 여행에서, 바다를 즐기는 여행으로",
            who: "물속에 오래 머물며 바다의 풍경을 깊이 보고 싶은 분",
            desc: "지구의 70%를 차지하는 바다, 그 안으로 직접 들어가는 선택입니다.",
            cta: "스쿠버다이빙 커리큘럼 보기",
        },
        page: {
            seoTitle: "더딥스 스쿠버다이빙 커리큘럼",
            seoDescription:
                "체험 스쿠버부터 오픈워터 · 어드밴스 · 레스큐/다이브마스터까지. 더딥스 스쿠버다이빙 단계별 커리큘럼을 안내합니다.",
            eyebrow: "SCUBA DIVING CURRICULUM GUIDE",
            headline: {
                lead: "스쿠버다이빙, 어떤 과정을",
                rest: "신청해야 할지 ",
                accent: "모르겠다면?",
            },
            lede: [
                "경험을 넘어, 성장으로 이어지는 다이빙.",
                "체험부터 순서대로 따라오시면 됩니다.",
            ],
            quickNav: [
                { id: "s-try", em: "체험", label: "체험 스쿠버" },
                { id: "s-ow", em: "LV.1", label: "오픈워터", hot: true },
                { id: "s-adv", em: "LV.2", label: "어드밴스" },
                { id: "s-pro", em: "PRO", label: "레스큐 이상" },
            ],
            surface: "0m · 수면",
            blocks: [
                {
                    kind: "step",
                    id: "s-try",
                    zone: "light",
                    node: "·",
                    tag: "EXPERIENCE",
                    title: "체험 스쿠버",
                    sub: "나의 첫 스쿠버",
                    rows: [
                        {
                            k: "내용",
                            v: "전 과정 강사 밀착 관리 · 장비 착용부터 호흡까지, 자격 과정 없이 즐기는 입문 체험",
                        },
                        { k: "포함", v: "체험 인솔 및 안전교육 · 사진 혹은 영상 촬영" },
                        { k: "별도 비용", v: "잠수풀 사용 실비 (입장료 · 공기통 · 장비 등)" },
                    ],
                    rec: "스쿠버가 처음이거나, 자격 과정이 부담스러운 분 추천!",
                    button: { label: "체험 스쿠버 보기", to: "/class/detail/16", tone: "light" },
                },
                {
                    kind: "step",
                    id: "s-ow",
                    zone: "light",
                    variant: "flagship",
                    node: "1",
                    depth: { value: "-18m", label: "DEPTH", tone: "light" },
                    badge: "✦ 가장 많이 선택하는 과정",
                    tag: "STEP 1",
                    title: "오픈워터 다이버",
                    rows: [
                        {
                            k: "핵심 학습",
                            v: "장비 사용과 세팅 · 수중 호흡과 중성부력 · 안전 잠수 절차",
                        },
                        {
                            k: "구성",
                            v: "이론 + 제한수역 **2타임** · 해양 교육 · 영상 촬영 및 피드백",
                        },
                        { k: "취득 자격", v: "최대 수심 **18m**까지 다이빙 가능" },
                        {
                            k: "별도 비용",
                            v: "협회 등록비(발급비) · 잠수풀 사용 실비 · 해양 교육비 및 개인 실비",
                        },
                    ],
                    benefit: { text: "이 자격부터 국내외 다이빙 투어 참여 가능" },
                    rec: "스쿠버 다이빙의 시작이 되는 입문 과정.\n바다를 향한 첫걸음을 여기서 시작하세요!",
                    button: { label: "오픈워터 과정 보기", to: "/class/detail/12", tone: "gold" },
                },
                {
                    kind: "step",
                    id: "s-adv",
                    zone: "dark",
                    node: "2",
                    depth: { value: "-30m", label: "DEPTH", tone: "white" },
                    tag: "STEP 2",
                    title: "어드밴스 다이버",
                    rows: [
                        {
                            k: "핵심 학습",
                            v: "다양한 환경과 수심에서의 실전 감각 · 응용 능력 · 상황별 대응",
                        },
                        { k: "구성", v: "이론 + 개방수역(해양교육) · 영상 촬영 및 피드백" },
                        { k: "신청 조건", v: "**오픈워터 다이버** 이상 자격 소지자" },
                        { k: "취득 자격", v: "최대 수심 **30m**까지 다이빙 가능" },
                        {
                            k: "별도 비용",
                            v: "협회 등록비(발급비) · 잠수풀 사용 실비 · 해양 개인 실비",
                        },
                    ],
                    rec: "경험을 넓히고, 다이빙의 폭을 확장하는 심화 과정.\n갈 수 있는 바다가 훨씬 넓어집니다.",
                    button: { label: "어드밴스 과정 보기", to: "/class/detail/60", tone: "deep" },
                },
                {
                    kind: "step",
                    id: "s-pro",
                    zone: "dark",
                    variant: "final",
                    node: "3",
                    violetNode: true,
                    tag: "PROFESSIONAL",
                    title: "레스큐 · 다이브마스터",
                    paragraphs: [
                        "단순 레벨업이 아닌, 진짜 다이버로 성장하는 전환점.\n실종 다이버 수색, 패닉 다이버 대응, 응급 상황 관리를 배우고\n프로페셔널 트랙으로 이어집니다.",
                    ],
                    footnote:
                        "※ 레스큐 과정은 유효한 응급처치(EFR 등) 자격이 필요하며,\n레스큐 과정과 동시에 연계 진행도 가능합니다.",
                    button: {
                        label: "카톡으로 문의하기",
                        href: "http://pf.kakao.com/_hVnQs/chat",
                        tone: "violet",
                    },
                },
            ],
            apartSections: [
                {
                    head: "더딥스 스쿠버를 **선택해야 하는 이유**",
                    special: {
                        tone: "teukgang",
                        chip: "국내 + 해외 연계형 교육",
                        title: "경험을 넘어, 성장으로",
                        desc: "국내에서 이론과 제한수역을 마치고, 따뜻하고 예쁜 해외 바다에서 해양 실습을 이어갈 수 있습니다.",
                        items: [
                            {
                                t: "자체 해외 센터",
                                d: "필리핀 보홀, 인도네시아 마나도 등 더딥스 직영 다이빙 센터 운영",
                            },
                            {
                                t: "국내외 연계형",
                                d: "따뜻하고 예쁜 해외 바다에서의 해양 실습 기회",
                            },
                            {
                                t: "활발한 커뮤니티",
                                d: "자격 취득 후에도 자연스럽게 다이빙을 이어갈 수 있는 환경",
                            },
                        ],
                    },
                },
            ],
            faqSections: [
                {
                    title: "자주 묻는 질문",
                    items: [
                        {
                            q: "수영을 못해도 배울 수 있나요?",
                            a: "네, 걱정하지 않으셔도 됩니다. 스쿠버는 장비가 호흡을 도와주기 때문에 수영 실력보다 편안하게 힘을 빼는 것이 더 중요해요. 강사가 전 과정을 옆에서 함께하니 물이 무서우셔도 안심하고 시작하실 수 있습니다.",
                        },
                        {
                            q: "체험만 하고 자격증은 나중에 따도 되나요?",
                            a: "네. 체험 스쿠버는 자격 과정과 별개로 운영되니 부담 없이 먼저 경험해 보시고, 마음에 드시면 오픈워터로 이어가시면 됩니다.",
                        },
                        {
                            q: "오픈워터를 마친 뒤 어드밴스는 언제 들으면 좋나요?",
                            a: "정해진 기간은 없습니다. 다만 오픈워터 후 감각이 남아 있을 때 이어가시면 훨씬 수월하고, 어드밴스를 마치면 수심 30m까지 확장되어 갈 수 있는 다이빙 포인트가 크게 늘어납니다.",
                        },
                        {
                            q: "수업료 외에 추가로 드는 비용이 있나요?",
                            a: "협회 등록비(발급비), 잠수풀 사용 실비(입장료·공기통·장비 등), 해양 교육비 및 개인 실비가 별도입니다. 수강료와 실비 금액은 신청 페이지에서 확인하실 수 있습니다.",
                        },
                        {
                            q: "해양 실습은 어디에서 진행되나요?",
                            a: "국내 해양 또는 더딥스 해외 센터(필리핀 보홀, 인도네시아 마나도 등)에서 진행됩니다. 일정에 따라 다르니 신청 시 확인해 주세요.",
                        },
                        {
                            q: "프리다이빙 자격이 있는데 도움이 되나요?",
                            a: "네. 이미 물과 장비에 친숙하시기 때문에 호흡과 중성부력 감각을 훨씬 빠르게 잡으시는 편입니다.",
                        },
                    ],
                },
            ],
            note: [
                "더딥스의 교육은 PADI · SDI 등 다양한 협회 과정으로 진행됩니다.",
                "※ 과정 배정과 진급은 담당 강사의 평가에 따라 안내됩니다.",
            ],
        },
    },

    /* ===================== 머메이드 ===================== */
    {
        slug: "mermaid",
        theme: "mermaid",
        darkCard: true,
        name: { ko: "머메이드", en: "MERMAID DIVING" },
        photo: mermaidPhoto,
        hubCard: {
            slogan: "한 번쯤 꿈꿔온 순간, 오늘은 당신이 인어공주입니다",
            who: "물속에서의 나를 가장 아름다운 순간으로 남기고 싶은 분",
            desc: "모노핀과 테일을 착용하고, 사진과 영상으로 남기는 수중 클래스입니다.",
            cta: "머메이드 커리큘럼 보기",
        },
        page: {
            seoTitle: "더딥스 머메이드 커리큘럼",
            seoDescription:
                "머메이드 원데이 체험부터 베이직 · 베이직+머메이드 · 어드밴스 · 어린이 과정까지. 더딥스 머메이드 커리큘럼을 안내합니다.",
            eyebrow: "MERMAID CURRICULUM GUIDE",
            headline: {
                lead: "머메이드, 어떤 과정을",
                rest: "신청해야 할지 ",
                accent: "모르겠다면?",
            },
            lede: [
                "한 번쯤 꿈꿔온 순간을 위한 과정입니다.",
                "체험부터 순서대로 따라오시면 됩니다.",
            ],
            quickNav: [
                { id: "s-oneday", em: "체험", label: "원데이" },
                { id: "s-basic", em: "LV.1", label: "베이직" },
                { id: "s-bm", em: "BEST", label: "베이직+머메이드", hot: true },
                { id: "s-adv", em: "LV.2", label: "어드밴스" },
                { id: "s-kids", em: "KIDS", label: "어린이" },
            ],
            surface: "START",
            blocks: [
                {
                    kind: "step",
                    id: "s-oneday",
                    zone: "light",
                    node: "·",
                    tag: "EXPERIENCE",
                    title: "머메이드 원데이",
                    sub: "체험",
                    rows: [
                        {
                            k: "핵심 학습",
                            v: "머메이드 호흡법 · 장비 착용 · 머메이드 피닝 기술 · 입수법",
                        },
                        { k: "구성", v: "잠수풀 **1회 (2시간)**" },
                        { k: "장비", v: "머메이드핀 **무료 대여**" },
                    ],
                    rec: "꿈꿔왔던 인어공주가 되어보는 하루.\n머메이드가 어떤 건지 먼저 경험해 보고 싶은 분 추천!",
                    button: { label: "원데이 클래스 보기", to: "/class/detail/15", tone: "light" },
                },
                {
                    kind: "step",
                    id: "s-basic",
                    zone: "light",
                    node: "1",
                    tag: "STEP 1",
                    title: "베이직",
                    sub: "머메이드 입문 단계",
                    rows: [
                        {
                            k: "핵심 학습",
                            v: "머메이드 호흡법 · 장비 착용 · 피닝 기술 · 입수법\n쥐풀기 · 백플로팅 · 백글라이드 입수 · 옆으로 피닝 · 레스큐",
                        },
                        { k: "구성", v: "잠수풀 **1회 (3시간)**" },
                    ],
                    rec: "머메이드 입문 정도만 배워보고 싶은 분,\n물 공포증이 있다면 이 과정부터 시작하세요!",
                    button: { label: "베이직 과정 보기", to: "/class/detail/21", tone: "light" },
                },
                {
                    kind: "step",
                    id: "s-bm",
                    zone: "light",
                    variant: "flagship",
                    node: "★",
                    badge: "✦ 가장 인기 많은 정석 코스",
                    tag: "BEST COURSE",
                    title: "베이직 + 머메이드",
                    rows: [
                        {
                            k: "핵심 학습",
                            v: "머메이드 호흡법 · 장비 착용\n쥐풀기 · 백플로팅 · 백글라이드 입수 · 옆으로 피닝 · 공중제비 · 버블\n전체 스킬 체크 · U턴 · C턴 · 핸드쉐이킹 · 깊은물 출수 · 머메이드롤 동작",
                        },
                        { k: "구성", v: "총 **3회** — 잠수풀 2회 + 이론 1회" },
                        {
                            k: "취득 자격",
                            v: "머메이드 다이버 자격 취득 — **어드밴스 진입 가능**",
                        },
                    ],
                    benefit: { text: "입문과 머메이드 과정을 한 번에, 어드밴스까지 이어집니다" },
                    rec: "본격적인 머메이드 다이버가 되기 위한 과정.\n제대로 시작할 생각이라면 이 코스를 추천해요!",
                    button: { label: "베이직+머메이드 보기", to: "/class/detail/22", tone: "gold" },
                },
                {
                    kind: "step",
                    id: "s-adv",
                    zone: "dark",
                    violet: true,
                    node: "2",
                    tag: "STEP 2",
                    title: "어드밴스",
                    sub: "머메이드의 꽃",
                    rows: [
                        {
                            k: "핵심 학습",
                            v: "전체 스킬 체크 · 레스큐 · 토잉 · 30m 다이나믹 · 팀 퍼포먼스 구상\n아웃핏 착용 후 스킬 · 레스큐 · 비상 탈출\n아웃핏 착용 후 개방수역 · 팀 퍼포먼스",
                        },
                        { k: "구성", v: "총 **3회** — 잠수풀 3회" },
                        { k: "신청 조건", v: "**머메이드 과정 이수자**만 등록 가능" },
                    ],
                    rec: "머메이드 기술을 심화하는 단계.\n퍼포먼스와 아웃핏까지, 진짜 인어가 되는 과정.",
                    button: { label: "어드밴스 과정 보기", to: "/class/detail/23", tone: "violet" },
                },
            ],
            apartSections: [
                {
                    id: "s-kids",
                    head: "우리 아이를 위한 **어린이 정규과정**",
                    special: {
                        tone: "teukgang",
                        chip: "KIDS · 어린이 전용 정규과정",
                        title: "어린이 머메이드",
                        copy: "아이도 인어공주가 될 수 있어요.",
                        desc: "성인 과정과 별도로 운영되는 어린이 전용 정규 과정입니다.\n아이의 수준과 속도에 맞춰 안전하게 진행됩니다.",
                        button: {
                            label: "어린이 머메이드 보기",
                            to: "/class/detail/24",
                            tone: "light",
                        },
                    },
                },
            ],
            faqSections: [
                {
                    title: "포함사항 / 불포함사항",
                    items: [
                        {
                            q: "수강료에 포함되는 것",
                            a: "영상 촬영 및 피드백\n수업 시 머메이드 전용 장비 렌탈\n무료 정기 트레이닝 (베이직 이상)\n각종 소모임 활동 기회",
                            open: true,
                        },
                        {
                            q: "별도로 드는 비용",
                            a: "협회 등록비(발급비)\n잠수풀 입장료\n슈트 대여 (선택사항)",
                        },
                    ],
                },
                {
                    title: "자주 묻는 질문",
                    items: [
                        {
                            q: "물을 무서워하는데 괜찮을까요?",
                            a: "물론입니다. 더딥스 머메이드 강사님들이 물 적응부터 차근차근 알려드리며, 정기 트레이닝을 자주 이용하시면 금방 적응하고 극복하실 수 있습니다.",
                        },
                        {
                            q: "준비물은 어떻게 필요한가요?",
                            a: "머메이드 전용 장비는 수업 시 강사님들이 대여해 드리며, 수영복과 수모, 수건 및 개인 샤워도구를 준비해 주시면 됩니다. 자세한 정보는 수업 전 강사님이 안내해 드리고 있습니다.",
                        },
                        {
                            q: "어느 과정으로 등록해야 하나요?",
                            a: "**원데이 클래스** — 머메이드가 어떤 건지 경험해 보고 싶은 분께 추천, 약 2시간 과정입니다.\n\n**베이직** — 머메이드 입문 정도만 배워보고자 하는 분께 추천드리며, 물 공포증이 있으신 분은 이 과정부터 시작하는 것을 추천드립니다.\n\n**베이직 + 머메이드** — 입문과 머메이드 과정을 같이 할 수 있는 코스이며, 본격적인 머메이드 다이버가 되기 위한 과정입니다.\n\n**어드밴스** — 머메이드 과정을 이수해야만 등록하실 수 있는 코스로, 머메이드 기술을 심화하는 과정입니다.",
                        },
                        {
                            q: "동반 할인을 받을 수 있는 기준은 뭔가요?",
                            a: "일행과 수업을 동시에 듣는 기준으로 할인이 적용됩니다.",
                        },
                        {
                            q: "수업 횟수를 꼭 확인해 주세요.",
                            a: "겉보기엔 저렴해 보여도 실제로는 수업 일수를 축소하는 경우가 있습니다. 더딥스는 베이직 1회(3시간), 베이직+머메이드 3회(이론 1회 포함), 어드밴스 3회로 진행됩니다.",
                        },
                    ],
                },
            ],
            note: ["※ 과정 배정과 진급은 담당 강사의 평가에 따라 안내됩니다."],
        },
    },
];

/** slug로 종목을 찾는다. 없으면 undefined. */
export const findCurriculumSport = (slug) =>
    CURRICULUM_SPORTS.find((sport) => sport.slug === slug);
